/**
 * services/crossTimeframe.ts
 *
 * Lightweight cross-timeframe context layer for MER Algo Footprints v4.2+
 *
 * Philosophy:
 * - Never fetch additional data — works with what's already loaded
 * - Derive higher-timeframe context by aggregating intraday bars
 * - Surface as context, not chart clutter
 * - Be honest when insufficient data exists
 *
 * Supported pairs:
 *   1min bars  → derive 5min and 15min context
 *   5min bars  → derive 15min and 30min context
 *   15min bars → derive 30min and 60min context
 *
 * Outputs used by:
 *   - promptBuilder (AI gets HTF context)
 *   - ThesisPanel (scenario quality)
 *   - setupEngine (setup confirmation)
 *   - DecisionDashboard (ranking quality)
 */

import { logDiagnostic } from '../../../diagnostics/diagnosticsStore';
import { Candle } from '../../../types';
import { calcVWAP } from '../../../utils/calc';

// ─── Types ────────────────────────────────────────────────────────────────────

export type TimeframePair =
  | '1min→5min'
  | '1min→15min'
  | '5min→15min'
  | '5min→30min'
  | '15min→30min'
  | '15min→60min';

export interface HigherTimeframeBar {
  ts:     number;
  open:   number;
  high:   number;
  low:    number;
  close:  number;
  volume: number;
}

export type HTFTrend = 'bullish' | 'bearish' | 'neutral' | 'insufficient_data';
export type HTFStructure = 'uptrend' | 'downtrend' | 'ranging' | 'breakout' | 'unknown';

export interface CrossTimeframeContext {
  // Which pair was derived
  baseTf:   string;
  higherTf: string;
  pair:     TimeframePair;

  // Higher-timeframe bars derived from base
  htfBars:  HigherTimeframeBar[];
  htfCount: number;

  // HTF analysis
  htfTrend:     HTFTrend;
  htfStructure: HTFStructure;
  htfVWAP:      number | null;

  // Alignment
  aligned:        boolean;    // base TF trend matches HTF trend
  alignmentNote:  string;     // plain English description
  structureNote:  string;     // what HTF structure says about the setup

  // Key HTF levels
  htfHighs:    number[];      // recent HTF swing highs
  htfLows:     number[];      // recent HTF swing lows
  htfMidpoint: number | null; // midpoint of recent HTF range

  // Data quality
  hasEnoughBars: boolean;     // enough bars to derive meaningful HTF
  confidence:    'high' | 'medium' | 'low' | 'insufficient';
}

// ─── Bar aggregation factors ──────────────────────────────────────────────────

const AGGREGATION: Record<TimeframePair, number> = {
  '1min→5min':   5,
  '1min→15min':  15,
  '5min→15min':  3,
  '5min→30min':  6,
  '15min→30min': 2,
  '15min→60min': 4,
};

const MIN_HTF_BARS = 8;    // minimum HTF bars for meaningful analysis
const IDEAL_HTF_BARS = 20; // ideal for trend detection

// ─── Main function ─────────────────────────────────────────────────────────────

export function buildCrossTimeframeContext(
  baseBars: Candle[],
  interval: string,
  baseTrend: 'bullish' | 'bearish' | 'neutral'
): CrossTimeframeContext | null {
  const pair = selectPair(interval);
  if (!pair) return null;

  const factor = AGGREGATION[pair];
  const [baseTf, higherTf] = pair.split('→');

  // Need enough base bars to derive meaningful HTF bars
  if (baseBars.length < factor * MIN_HTF_BARS) return null;

  // Aggregate base bars into HTF bars
  const htfBars = aggregateBars(baseBars, factor);
  if (htfBars.length < MIN_HTF_BARS) return null;

  // Analyse HTF structure
  const htfTrend     = detectHTFTrend(htfBars);
  const htfStructure = detectHTFStructure(htfBars);
  const htfVWAP      = deriveHTFVWAP(htfBars);
  const { highs, lows } = detectHTFSwings(htfBars);

  const htfMidpoint = highs.length && lows.length
    ? (Math.max(...highs) + Math.min(...lows)) / 2
    : null;

  // Alignment check
  const aligned = (
    (baseTrend === 'bullish' && htfTrend === 'bullish') ||
    (baseTrend === 'bearish' && htfTrend === 'bearish') ||
    (baseTrend === 'neutral' && htfTrend === 'neutral')
  );

  const alignmentNote = buildAlignmentNote(baseTrend, htfTrend, baseTf!, higherTf!, aligned);
  const structureNote = buildStructureNote(htfStructure, higherTf!);

  const confidence: CrossTimeframeContext['confidence'] =
    htfBars.length >= IDEAL_HTF_BARS ? 'high' :
    htfBars.length >= MIN_HTF_BARS * 1.5 ? 'medium' :
    htfBars.length >= MIN_HTF_BARS ? 'low' : 'insufficient';

  logDiagnostic('info', 'htf_derived', `HTF derived (${pair}, ${htfBars} bars)`, undefined, pair);
  return {
    baseTf:    baseTf!,
    higherTf:  higherTf!,
    pair,
    htfBars,
    htfCount:  htfBars.length,
    htfTrend,
    htfStructure,
    htfVWAP,
    aligned,
    alignmentNote,
    structureNote,
    htfHighs:  highs.slice(-5),
    htfLows:   lows.slice(-5),
    htfMidpoint,
    hasEnoughBars: htfBars.length >= MIN_HTF_BARS,
    confidence,
  };
}

// ─── Interval → pair selection ─────────────────────────────────────────────────

function selectPair(interval: string): TimeframePair | null {
  // Normalize common interval strings
  const norm = interval.toLowerCase().replace(/\s/g, '');
  if (norm === '1min' || norm === '1m')  return '1min→5min';
  if (norm === '5min' || norm === '5m')  return '5min→15min';
  if (norm === '15min' || norm === '15m') return '15min→60min';
  // Tick/second intervals — aggregate to 1min first (handled by caller)
  if (norm.includes('sec') || norm.includes('tick')) return null;
  return null;
}

// ─── Bar aggregation ───────────────────────────────────────────────────────────

function aggregateBars(base: Candle[], factor: number): HigherTimeframeBar[] {
  const htf: HigherTimeframeBar[] = [];

  for (let i = 0; i + factor <= base.length; i += factor) {
    const slice = base.slice(i, i + factor);
    htf.push({
      ts:     slice[0].ts,
      open:   slice[0].open,
      high:   Math.max(...slice.map(c => c.high)),
      low:    Math.min(...slice.map(c => c.low)),
      close:  slice[slice.length - 1].close,
      volume: slice.reduce((s, c) => s + c.volume, 0),
    });
  }

  return htf;
}

// ─── Trend detection ───────────────────────────────────────────────────────────

function detectHTFTrend(bars: HigherTimeframeBar[]): HTFTrend {
  if (bars.length < 6) return 'insufficient_data';

  const recent = bars.slice(-8);
  const closes = recent.map(b => b.close);

  // Simple linear regression slope
  const n = closes.length;
  const xMean = (n - 1) / 2;
  const yMean = closes.reduce((s, v) => s + v, 0) / n;
  let numerator = 0, denominator = 0;
  closes.forEach((y, i) => {
    numerator   += (i - xMean) * (y - yMean);
    denominator += (i - xMean) ** 2;
  });
  const slope = denominator !== 0 ? numerator / denominator : 0;

  // Slope as % of price per bar — normalize for threshold
  const slopePct = (slope / yMean) * 100;

  if (slopePct >  0.08) return 'bullish';
  if (slopePct < -0.08) return 'bearish';
  return 'neutral';
}

// ─── Structure detection ───────────────────────────────────────────────────────

function detectHTFStructure(bars: HigherTimeframeBar[]): HTFStructure {
  if (bars.length < 8) return 'unknown';

  const recent = bars.slice(-10);
  const highs  = recent.map(b => b.high);
  const lows   = recent.map(b => b.low);

  const hh = highs[highs.length-1] > highs[highs.length-3] &&
             highs[highs.length-3] > highs[highs.length-5];
  const hl = lows[lows.length-1]   > lows[lows.length-3] &&
             lows[lows.length-3]   > lows[lows.length-5];
  const lh = highs[highs.length-1] < highs[highs.length-3] &&
             highs[highs.length-3] < highs[highs.length-5];
  const ll = lows[lows.length-1]   < lows[lows.length-3] &&
             lows[lows.length-3]   < lows[lows.length-5];

  // Check for compression (range contraction)
  const rangeRecent = highs[highs.length-1] - lows[lows.length-1];
  const rangeOld    = highs[0] - lows[0];
  const contracting = rangeRecent < rangeOld * 0.6;

  if (hh && hl) return contracting ? 'breakout' : 'uptrend';
  if (lh && ll) return 'downtrend';
  return 'ranging';
}

// ─── Swing detection ──────────────────────────────────────────────────────────

function detectHTFSwings(bars: HigherTimeframeBar[]): { highs: number[]; lows: number[] } {
  const highs: number[] = [];
  const lows:  number[] = [];
  const lookback = 2;

  for (let i = lookback; i < bars.length - lookback; i++) {
    const h = bars[i].high;
    const l = bars[i].low;
    if (bars.slice(i - lookback, i).every(b => b.high < h) &&
        bars.slice(i + 1, i + lookback + 1).every(b => b.high < h)) {
      highs.push(h);
    }
    if (bars.slice(i - lookback, i).every(b => b.low > l) &&
        bars.slice(i + 1, i + lookback + 1).every(b => b.low > l)) {
      lows.push(l);
    }
  }

  return { highs, lows };
}

// ─── HTF VWAP ─────────────────────────────────────────────────────────────────

function deriveHTFVWAP(bars: HigherTimeframeBar[]): number | null {
  if (!bars.length) return null;
  const candles = bars.map(b => ({
    ts: b.ts, open: b.open, high: b.high, low: b.low, close: b.close, volume: b.volume,
  }));
  const vwapArr = calcVWAP(candles);
  return vwapArr[vwapArr.length - 1] ?? null;
}

// ─── Narrative builders ────────────────────────────────────────────────────────

function buildAlignmentNote(
  baseTrend: string, htfTrend: HTFTrend,
  baseTf: string, higherTf: string, aligned: boolean
): string {
  if (htfTrend === 'insufficient_data') {
    return `Insufficient ${higherTf} bars for trend analysis.`;
  }

  const htfLabel = htfTrend === 'neutral' ? 'neutral/ranging' : htfTrend;
  const baseLabel = baseTrend === 'neutral' ? 'neutral' : baseTrend;

  if (aligned && baseTrend !== 'neutral') {
    return `✓ Aligned: ${baseTf} ${baseLabel} matches ${higherTf} ${htfLabel} trend — higher-quality setup context.`;
  }
  if (!aligned && baseTrend !== 'neutral' && htfTrend !== 'neutral') {
    return `⚠ Divergence: ${baseTf} ${baseLabel} vs ${higherTf} ${htfLabel} — counter-trend trades carry additional risk.`;
  }
  return `${higherTf} trend is ${htfLabel}. ${baseTf} operating in ${baseLabel} mode within this context.`;
}

function buildStructureNote(structure: HTFStructure, higherTf: string): string {
  switch (structure) {
    case 'uptrend':   return `${higherTf} in confirmed uptrend (HH/HL). Long setups have structural tailwind.`;
    case 'downtrend': return `${higherTf} in confirmed downtrend (LH/LL). Short setups have structural tailwind.`;
    case 'ranging':   return `${higherTf} ranging — mean-reversion setups preferred. Breakout attempts carry fade risk.`;
    case 'breakout':  return `${higherTf} shows range compression → potential breakout forming. Watch for expansion.`;
    default:          return `${higherTf} structure unclear — insufficient swing history.`;
  }
}

// ─── Compact summary for AI prompt injection ───────────────────────────────────

export function formatCTFForPrompt(ctx: CrossTimeframeContext): string {
  if (!ctx.hasEnoughBars) return '';

  return `CROSS-TIMEFRAME CONTEXT (${ctx.baseTf} / ${ctx.higherTf} — ${ctx.confidence} confidence)
${ctx.higherTf} trend: ${ctx.htfTrend.toUpperCase()}
${ctx.higherTf} structure: ${ctx.htfStructure}
Alignment: ${ctx.aligned ? 'ALIGNED ✓' : 'DIVERGENT ⚠'}
${ctx.alignmentNote}
${ctx.structureNote}${ctx.htfHighs.length ? `\n${ctx.higherTf} recent highs: ${ctx.htfHighs.map(h => h.toFixed(2)).join(', ')}` : ''}${ctx.htfLows.length ? `\n${ctx.higherTf} recent lows: ${ctx.htfLows.map(l => l.toFixed(2)).join(', ')}` : ''}`;
}
